import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, isAdmin } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Hammer, KeyRound, Home, ShieldCheck, Wrench, FileText, Bell, ArrowRight,
  Building2, Sparkles, ChevronRight, Check, Search,
  MessageCircle, UserRound, Wind, Droplets,
} from "lucide-react";

const HERO_VIDEO = "https://customer-assets-jai6qajn.emergentagent.net/job_warranty-hub-56/artifacts/2lw5frpm_Arqitek%20Hero.mp4";

const PHASES = ["Land Development", "Foundation", "Framing", "Roofing", "Windows & Exterior", "MEP Rough-In", "Drywall", "Interior Finishes", "Final Walkthrough", "Move-In"];

const QA = [
  { q: "What paint is in the living room?", a: "Sherwin-Williams · Urbane Bronze (SW 7048)", tag: "Paint" },
  { q: "When does my HVAC warranty expire?", a: "Carrier Infinity · Active — ~9 years remaining", tag: "HVAC" },
  { q: "Who installed my water heater?", a: "Lone Star Plumbing Co. · Rheem 50-gal hybrid", tag: "Appliances" },
  { q: "When was my roof installed?", a: "GAF Timberline HDZ · charcoal · Apr 2026", tag: "Roofing" },
];

const LIFECYCLE = [
  { k: "Build", icon: Hammer, d: "Follow construction from land to move-in with live progress, milestones, and photos." },
  { k: "Close", icon: KeyRound, d: "Receive every plan, permit, and closing document in one secure place." },
  { k: "Own", icon: Home, d: "Understand exactly what was installed — every product, finish, and system." },
  { k: "Protect", icon: ShieldCheck, d: "Track builder, structural, and manufacturer warranties before they lapse." },
  { k: "Service", icon: Wrench, d: "Request and track repairs with photos, scheduling, and full history." },
];

const FEATURES = [
  { icon: Hammer, t: "Build Progress", d: "A visual timeline and phase tracker so you always know what's happening with your home." },
  { icon: Home, t: "Digital Home Record", d: "A permanent record of every appliance, material, and finish — searchable years later." },
  { icon: ShieldCheck, t: "Warranty Center", d: "Clear Active / Expiring / Expired states with remaining coverage at a glance." },
  { icon: Wrench, t: "Service Requests", d: "Submit issues with photos, then follow status, appointments, and resolution." },
  { icon: FileText, t: "Document Center", d: "Plans, permits, manuals, and closing docs — organized and downloadable." },
  { icon: Bell, t: "Notifications", d: "Stay informed across construction, warranty, and service milestones." },
];

const FLOW = ["Construction History", "Specifications", "Installed Products", "Documents", "Warranty", "Service History", "Permanent Digital Record"];

function Section({ children, className = "" }) {
  return <section className={`max-w-6xl mx-auto px-5 sm:px-8 ${className}`}>{children}</section>;
}

function PhaseTracker() {
  const [lit, setLit] = useState(() => new Set());
  const refs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      setLit((prev) => {
        const next = new Set(prev);
        entries.forEach((e) => { if (e.isIntersecting) next.add(Number(e.target.dataset.i)); });
        return next;
      });
    }, { threshold: 0.6, rootMargin: "0px 0px -25% 0px" });
    refs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const maxLit = lit.size ? Math.max(...lit) : -1;
  const pct = Math.round(((maxLit + 1) / PHASES.length) * 100);
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-8">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Construction Progress</span>
        <span className="font-display text-4xl text-arq-teal tabular-nums transition-all duration-500">{pct}%</span>
      </div>
      <div className="relative pl-8">
        <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border overflow-hidden rounded-full">
          <div className="w-full bg-arq-teal transition-[height] duration-700 ease-out" style={{ height: `${pct}%` }} />
        </div>
        {PHASES.map((p, i) => {
          const on = lit.has(i);
          return (
            <div key={p} ref={(el) => (refs.current[i] = el)} data-i={i} className="relative flex items-center gap-4 py-[14px]">
              <span className={`absolute -left-8 h-6 w-6 rounded-full border flex items-center justify-center transition-all duration-500 ${on ? "bg-arq-teal border-arq-teal shadow-[0_0_18px_rgba(62,221,166,0.55)] scale-100" : "bg-arq-card border-border scale-90"}`}>
                {on ? <Check className="h-3.5 w-3.5 text-[#08110D]" strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />}
              </span>
              <span className={`text-lg sm:text-xl transition-all duration-500 ${on ? "text-arq-ink translate-x-0" : "text-muted-foreground/40 -translate-x-0"}`}>{p}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HomeQA() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % QA.length), 3200);
    return () => clearInterval(t);
  }, []);
  const cur = QA[i];
  const chips = ["HVAC", "Paint", "Flooring", "Roofing", "Windows", "Appliances"];
  return (
    <div className="bg-arq-card border border-border rounded-lg p-6 sm:p-8 shadow-2xl relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-52 w-52 rounded-full bg-arq-teal/10 blur-3xl pointer-events-none" />
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-5"><Sparkles className="h-3.5 w-3.5 text-arq-teal" />Ask Angela</div>
      <div className="flex items-center gap-3 bg-arq-surface border border-border rounded-md px-4 h-12">
        <Search className="h-4 w-4 text-arq-teal shrink-0" />
        <span key={"q" + i} className="text-sm text-arq-ink arq-fade-up truncate">{cur.q}</span>
      </div>
      <div key={"a" + i} className="mt-6 arq-fade-up min-h-[72px]">
        <p className="text-[10px] uppercase tracking-widest text-arq-teal mb-1.5">{cur.tag}</p>
        <p className="font-display text-2xl sm:text-3xl leading-snug">{cur.a}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-border">
        {chips.map((c) => {
          const active = cur.tag === c;
          return <span key={c} className={`text-xs px-2.5 py-1 rounded-full border transition-colors duration-500 ${active ? "border-arq-teal/50 text-arq-teal bg-arq-teal/10" : "border-border text-muted-foreground/70"}`}>{c}</span>;
        })}
      </div>
      <div className="flex gap-1.5 mt-6">
        {QA.map((_, idx) => <span key={idx} className={`h-1 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-arq-teal" : "w-1.5 bg-border"}`} />)}
      </div>
    </div>
  );
}

function AngelaChat() {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const msgs = [
    { icon: Wind, t: "Your HVAC filter is due this week — I've added it to your maintenance list." },
    { icon: Droplets, t: "It's been about 12 months — time to flush your Rheem tankless water heater." },
    { icon: ShieldCheck, t: "Good news: that drywall crack is covered by your builder warranty. Want me to open a service request?" },
  ];
  return (
    <div ref={ref} className="bg-arq-card border border-border rounded-lg shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-arq-surface/40">
        <div className="relative h-10 w-10 rounded-full bg-arq-teal/15 border border-arq-teal/40 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-arq-teal" />
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-arq-teal border-2 border-arq-card" />
        </div>
        <div>
          <p className="font-display text-lg leading-none">Angela</p>
          <p className="text-[11px] text-arq-teal mt-1">Your Home AI · Online</p>
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">Proactive</span>
      </div>
      <div className="p-5 space-y-3">
        {msgs.map((m, i) => {
          const Icon = m.icon;
          return (
            <div key={i} className={`flex items-start gap-3 ${show ? "arq-fade-up" : "opacity-0"}`} style={{ animationDelay: `${i * 400}ms` }}>
              <div className="h-8 w-8 rounded-full bg-arq-teal/10 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-arq-teal" /></div>
              <div className="bg-arq-surface border border-border rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-arq-ink max-w-[86%]">{m.t}</div>
            </div>
          );
        })}
        <div className={`flex items-center gap-3 pt-4 mt-1 border-t border-border ${show ? "arq-fade-up" : "opacity-0"}`} style={{ animationDelay: `${msgs.length * 400 + 250}ms` }}>
          <div className="h-8 w-8 rounded-full bg-arq-teal/20 flex items-center justify-center text-xs font-semibold text-arq-teal shrink-0">MR</div>
          <p className="text-sm text-arq-ink">Prefer a human? <span className="text-arq-teal">Marcus</span>, your dedicated Home Manager, is one tap away.</p>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const primaryCta = () => navigate(user ? (isAdmin(user) ? "/admin" : "/dashboard") : "/login");

  return (
    <div className="min-h-screen bg-arq-bg text-arq-ink overflow-x-hidden">
      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-arq-bg/80 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"}`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-5 sm:h-6 w-auto" data-testid="landing-logo" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/90">
            <a href="#lifecycle" className="hover:text-arq-teal transition-colors">Lifecycle</a>
            <a href="#angela" className="hover:text-arq-teal transition-colors">Ask Angela</a>
            <a href="#features" className="hover:text-arq-teal transition-colors">Features</a>
            <a href="#record" className="hover:text-arq-teal transition-colors">Home Record</a>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-sm text-white/90 hover:text-arq-teal transition-colors" data-testid="nav-signin">Sign in</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative min-h-[92vh] flex items-end">
        <video src={HERO_VIDEO} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,13,0.55) 0%, rgba(10,15,13,0.35) 35%, rgba(10,15,13,0.85) 78%, #0A0F0D 100%)" }} />
        <Section className="relative z-10 pb-20 pt-32 w-full">
          <div className="max-w-3xl arq-fade-up">
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-arq-teal mb-6 font-medium">ARQITRAK · Digital Homeownership Platform</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              Your home's entire story,<br /><span className="text-arq-teal">in one place.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-6 max-w-xl">
              Track your home. Track construction. Track service. Track your warranty. ARQITRAK is the permanent digital record and ownership platform for every Arqitek home.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button onClick={primaryCta} variant="outline" className="h-12 px-6 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white group" data-testid="hero-cta">
                {user ? "Open Dashboard" : "Enter Your Home"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <a href="#lifecycle"><Button variant="outline" className="h-12 px-6 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="hero-secondary">See how it works</Button></a>
            </div>
            <div className="flex items-center gap-2 mt-10 text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-muted-foreground/70 flex-wrap">
              {LIFECYCLE.map((s, i) => (
                <React.Fragment key={s.k}>
                  <span>{s.k}</span>{i < LIFECYCLE.length - 1 && <span className="text-arq-teal">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* Lifecycle */}
      <Section className="py-24" >
        <div id="lifecycle" className="scroll-mt-24">
          <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">The Ownership Lifecycle</p>
          <h2 className="font-display text-4xl sm:text-5xl max-w-2xl">One platform, from raw land to the home you own.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
            {LIFECYCLE.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.k} className="bg-arq-card border border-border rounded-md p-5 hover:border-arq-teal/50 transition-colors group" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="h-6 w-6 text-arq-teal" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
                  </div>
                  <p className="font-display text-xl">{s.k}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Build — scroll-lit phase tracker */}
      <Section className="py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">Build</p>
            <h3 className="font-display text-4xl sm:text-5xl">Watch your home take shape.</h3>
            <p className="text-muted-foreground leading-relaxed mt-4 max-w-md">Follow every phase — from raw land to move-in — with live completion percentages, a visual milestone timeline, and a growing gallery of construction photos and updates from your Arqitek team.</p>
            <ul className="mt-6 space-y-3">
              {["Live phase tracker with clear statuses", "Milestone timeline from land to move-in", "Chronological construction photo history"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm"><ChevronRight className="h-4 w-4 text-arq-teal shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
          <PhaseTracker />
        </div>
      </Section>

      {/* Own · Protect — ask-your-home panel */}
      <div className="bg-arq-card/30 border-y border-border">
        <Section className="py-20 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1"><HomeQA /></div>
            <div className="order-1 lg:order-2">
              <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">Own · Protect</p>
              <h3 className="font-display text-4xl sm:text-5xl">Know everything in your home — for decades.</h3>
              <p className="text-muted-foreground leading-relaxed mt-4 max-w-md">Even 20 years from now, know exactly what paint is in the living room, when the HVAC was installed, and whether the roof is still under warranty. Every product, finish, and system — permanently recorded and instantly searchable.</p>
              <ul className="mt-6 space-y-3">
                {["Detailed records for appliances, systems & finishes", "Warranties with Active / Expiring / Expired states", "Manuals and documents at your fingertips"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm"><ChevronRight className="h-4 w-4 text-arq-teal shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      </div>

      {/* Angela — AI Home Concierge */}
      <Section className="py-24">
        <div id="angela" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">Meet Angela</p>
            <h2 className="font-display text-4xl sm:text-5xl">Your home has someone looking after it.</h2>
            <p className="text-muted-foreground leading-relaxed mt-4 max-w-md">Angela is your personal home AI — she knows every detail of your home and watches over it for you. From air filters to your tankless water heater, she tells you what needs doing, exactly when it needs doing. And whenever you'd rather talk to a person, a dedicated Arqitek Home Manager is right there with you.</p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Bell, t: "Proactive maintenance", d: "Angela reminds you to change air filters, flush the water heater, and service the HVAC — right on schedule." },
                { icon: MessageCircle, t: "Instant answers, 24/7", d: "Ask anything about your home and get an answer immediately, day or night." },
                { icon: UserRound, t: "A dedicated Home Manager", d: "Every home includes a real Arqitek Home Manager for when you want a human touch." },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.t} className="flex gap-4">
                    <div className="h-10 w-10 rounded-md bg-arq-teal/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-arq-teal" strokeWidth={1.6} /></div>
                    <div><p className="font-medium text-arq-ink">{f.t}</p><p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{f.d}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
          <AngelaChat />
        </div>
      </Section>

      {/* Features grid */}
      <Section className="py-24">
        <div id="features" className="scroll-mt-24">
          <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">Everything Connected</p>
          <h2 className="font-display text-4xl sm:text-5xl max-w-2xl">Not customer-service software. A home ownership platform.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="bg-arq-card border border-border rounded-md p-6 hover:border-arq-teal/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
                  <div className="h-10 w-10 rounded-md bg-arq-teal/10 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-arq-teal" strokeWidth={1.6} />
                  </div>
                  <p className="font-display text-xl">{f.t}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* One record that grows */}
      <div className="bg-arq-card/40 border-y border-border">
        <Section className="py-24">
          <div id="record" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-arq-teal font-medium mb-3">The Differentiator</p>
              <h2 className="font-display text-4xl sm:text-5xl">One property record that grows over time.</h2>
              <p className="text-muted-foreground leading-relaxed mt-4 max-w-md">Construction, closing, warranty, and service aren't separate systems. Everything connects back to a single home — a permanent digital record that begins during construction and lasts as long as the home stands.</p>
              <Button onClick={primaryCta} className="mt-8 h-11 group" data-testid="record-cta">
                {user ? "Open Dashboard" : "Get Started"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-6 w-6 text-arq-teal relative z-10 -ml-[10px] bg-arq-bg" />
                <span className="font-display text-2xl">Property</span>
              </div>
              <div className="space-y-4">
                {FLOW.map((f, i) => (
                  <div key={f} className="flex items-center gap-4">
                    <span className="h-2.5 w-2.5 rounded-full bg-arq-teal/70 relative z-10 -ml-[3px]" />
                    <span className={`text-sm ${i === FLOW.length - 1 ? "text-arq-teal font-medium" : "text-muted-foreground"}`}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* CTA band */}
      <Section className="py-28 text-center">
        <h2 className="font-display text-4xl sm:text-6xl max-w-4xl mx-auto leading-[1.05]">
          Track your home. Track construction.<br /><span className="text-arq-teal">Track service. Track your warranty.</span>
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          <Button onClick={primaryCta} className="h-12 px-8 text-base group" data-testid="footer-cta">
            {user ? "Open Dashboard" : "Enter Your Home"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-border">
        <Section className="py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-5 w-auto opacity-80" />
          <p className="text-xs text-muted-foreground">A permanent digital record and ownership platform for the home.</p>
          <p className="text-xs text-muted-foreground/60">© {new Date().getFullYear()} Arqitek · ARQITRAK</p>
        </Section>
      </footer>
    </div>
  );
}
